<!-- ═══════════════════════════════════════════════════════════
     TMS Sidebar – elegant drawer with search, menu & user info
     ═══════════════════════════════════════════════════════════ -->
<ng-container *ngIf="router.url !== '/login'">
  <!-- Real gap found 2026-07-27: an interrupted open/close animation (e.g. instant
       navigation firing mid-transition) can leave the drawer's overlay mask stuck in
       a "leave-active" state that still intercepts pointer events after the drawer is
       visually gone — confirmed via CDP hit-test as the root cause of a prior offline
       freeze bug (see overlay-mask-sweep.ts). Animation removed entirely (0ms) rather
       than only patched reactively, closing off the whole class of stuck-mask bugs at
       the source instead of sweeping up after it. -->
  <p-drawer
    [(visible)]="isMenuExpand"
    [baseZIndex]="10000"
    [closeOnEscape]="true"
    [modal]="true"
    [dismissible]="true"
    appendTo="body"
    [position]="menuStyle.dockPhone() ? 'bottom' : 'left'"
    [style]="menuStyle.dockPhone() ? { width: '100%', height: '88%' } : { width: '18rem' }"
    [styleClass]="menuStyle.dockPhone() ? 'tms-sidebar tms-sheet' : 'tms-sidebar'"
    [transitionOptions]="'0ms'"
    (onHide)="onHide()"
    (onShow)="onShow()"
  >
    <!-- ── Header: the company. The person (photo, name, account pop-up) is the card at the bottom (footer). ── -->
    <ng-template pTemplate="header">
      <span class="tms-sheet-grab" *ngIf="menuStyle.dockPhone()" aria-hidden="true"></span>
      <!-- Top: the COMPANY (badge, name, ID). It opens the company pop-up: company ID to copy, switch company, company pages. -->
      <div
        class="tms-sb-ws"
        role="button"
        tabindex="0"
        aria-haspopup="menu"
        [attr.aria-label]="activeCompanyDisplayName || (userProfile?.companyId ?? '')"
        [attr.title]="activeCompanyDisplayName || null"
        (click)="workspaceMenu.toggle($event)"
        (keydown.enter)="workspaceMenu.toggle($event)"
        (keydown.space)="$event.preventDefault(); workspaceMenu.toggle($event)"
      >
        <span class="tms-sb-ws-badge" aria-hidden="true">{{ companyInitials }}</span>
        <span class="tms-sb-ws-text">
          <ng-container *ngIf="menuStyle.dockOn(); else classicCompany">
            <span class="tms-sb-ws-name tms-tier-main">{{ companyTier().main }}</span>
            <span class="tms-sb-ws-sub" *ngIf="companyTier().sub">{{ companyTier().sub }}</span>
          </ng-container>
          <ng-template #classicCompany>
            <span class="tms-sb-ws-name">{{ activeCompanyDisplayName || userProfile?.companyName || userProfile?.companyId || '' }}</span>
          </ng-template>
        </span>
        <i class="pi pi-chevron-down tms-sb-ws-caret" aria-hidden="true"></i>
      </div>
    </ng-template>

    <!-- ── Content: tools (fixed) + the menu list (the only part that scrolls) ─────────────────── -->
    <ng-template pTemplate="content">
      <div class="tms-sb-body">
        <!-- Tools in ONE row: "+ New" (only what this user may create; its pop-up sits outside the drawer, below) and the search -->
        <div class="tms-sb-toolbar">
          <p-button
            *ngIf="canCreateAnything()"
            [label]="'sidebar.new' | t"
            icon="pi pi-plus"
            styleClass="tms-sb-new-btn"
            size="small"
            aria-haspopup="menu"
            (onClick)="openNewMenu($event)"
          ></p-button>
          <p-iconField iconPosition="left" class="tms-sb-search-field">
            <p-inputIcon styleClass="pi pi-search tms-sb-search-icon"></p-inputIcon>
            <input
              #searchInput
              pInputText
              class="p-inputtext-sm w-full tms-sb-search"
              type="search"
              [(ngModel)]="searchString"
              (input)="filterMenuItems()"
              (keydown.arrowdown)="focusFirstMenuItem($event)"
              [placeholder]="'layout.searchMenu' | t"
            />
          </p-iconField>
        </div>

        <div class="tms-sb-scroll">
          <!-- Recent: the last pages opened (only pages still in this person's menu) -->
          <div class="tms-sb-recent" *ngIf="recentItems.length && !searching">
            <div class="tms-sb-recent-title"><i class="pi pi-history"></i><span>{{ 'sidebar.recent' | t }}</span></div>
            <button type="button" class="tms-sb-recent-item" *ngFor="let r of recentItems; trackBy: trackRecent" (click)="openRecent(r)">
              <ll-icon *ngIf="menuStyle.dockOn(); else recentPi" [name]="r.icon"></ll-icon>
              <ng-template #recentPi><i [class]="r.icon"></i></ng-template>
              <span class="tms-sb-recent-label">{{ r.label }}</span>
            </button>
          </div>

          <div class="tms-sb-recent tms-sheet-view" *ngIf="menuStyle.dockPhone() && menuStyle.routeAllowsDock() && !searching">
            <div class="tms-sb-recent-title"><ll-icon name="maximize"></ll-icon><span>{{ 'menuDock.viewGroup' | t }}</span></div>
            <button type="button" class="tms-sb-recent-item" (click)="hideBars('dock-hidden')">
              <ll-icon name="chevron-down"></ll-icon>
              <span class="tms-sb-recent-label">{{ 'menuDock.hideBar' | t }}</span>
            </button>
            <button type="button" class="tms-sb-recent-item" (click)="hideBars('focus')">
              <ll-icon name="maximize"></ll-icon>
              <span class="tms-sb-recent-label">{{ 'menuDock.focusMode' | t }}</span>
            </button>
          </div>

          <nav #navRef class="tms-nav" role="tree" (keydown)="onNavKeydown($event)" *ngIf="accessibleMenuItems && accessibleMenuItems.length > 0; else noMenu">
            <ng-container *ngFor="let it of accessibleMenuItems; trackBy: trackNav">
              <div class="tms-nav-heading" role="presentation" *ngIf="it.isHeading">{{ it.label }}</div>
              <ng-container *ngIf="!it.isHeading">
                <button
                  type="button"
                  class="tms-nav-row"
                  role="treeitem"
                  [class.is-active]="isNavActive(it)"
                  [attr.aria-expanded]="it.items?.length ? !!it.expanded : null"
                  [attr.aria-current]="isNavActive(it) ? 'page' : null"
                  [attr.title]="it.label"
                  (click)="onNavRowClick(it)"
                >
                  <ll-icon class="tms-nav-ico" *ngIf="menuStyle.dockOn(); else piIco" [name]="it.icon"></ll-icon>
                  <ng-template #piIco><i class="tms-nav-ico" [ngClass]="it.icon"></i></ng-template>
                  <ng-container *ngIf="!menuStyle.dockOn(); else tierLabel">
                    <span class="tms-nav-label"><ng-container *ngFor="let p of labelParts(it.label)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span>
                  </ng-container>
                  <ng-template #tierLabel>
                    @let tp = tierParts(it.label);
                    <span class="tms-nav-label tms-tier"><span class="t1"><ng-container *ngFor="let p of labelParts(tp.main)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span><span class="t2" *ngIf="tp.sub"><ng-container *ngFor="let p of labelParts(tp.sub)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span></span>
                  </ng-template>
                  <span class="tms-nav-badge" *ngIf="it.badge">{{ it.badge }}</span>
                  <i class="pi pi-chevron-down tms-nav-caret" [class.open]="it.expanded" *ngIf="it.items?.length"></i>
                </button>
                <div class="tms-nav-children" [class.open]="it.expanded" role="group" *ngIf="it.items?.length" [attr.inert]="it.expanded ? null : ''">
                  <div class="tms-nav-children-inner">
                    <button
                      type="button"
                      class="tms-nav-row tms-nav-sub"
                      role="treeitem"
                      *ngFor="let c of it.items; trackBy: trackNav"
                      [class.is-active]="isNavActive(c)"
                      [attr.aria-current]="isNavActive(c) ? 'page' : null"
                      [attr.title]="c.label"
                      (click)="onNavRowClick(c)"
                    >
                      <ll-icon class="tms-nav-ico" *ngIf="menuStyle.dockOn(); else piIcoSub" [name]="c.icon"></ll-icon>
                      <ng-template #piIcoSub><i class="tms-nav-ico" [ngClass]="c.icon"></i></ng-template>
                      <ng-container *ngIf="!menuStyle.dockOn(); else tierLabelSub">
                        <span class="tms-nav-label"><ng-container *ngFor="let p of labelParts(c.label)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span>
                      </ng-container>
                      <ng-template #tierLabelSub>
                    @let tp = tierParts(c.label);
                    <span class="tms-nav-label tms-tier"><span class="t1"><ng-container *ngFor="let p of labelParts(tp.main)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span><span class="t2" *ngIf="tp.sub"><ng-container *ngFor="let p of labelParts(tp.sub)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span></span>
                  </ng-template>
                      <span class="tms-nav-badge" *ngIf="c.badge">{{ c.badge }}</span>
                    </button>
                  </div>
                </div>
              </ng-container>
            </ng-container>
          </nav>

          <ng-template #noMenu>
            <div class="tms-sb-no-menu">
              <i class="pi pi-inbox tms-sb-no-menu-icon"></i>
              <p>{{ "layout.noMenuItems" | t }}</p>
            </div>
          </ng-template>
        </div>
      </div>
    </ng-template>

    <!-- ── Footer: Quick Logout + Version ──────────────────
         No avatar AND no name here — the account identity + full menu (including Logout)
         already live in the header card above; repeating either here was pure duplication
         (real gap found 2026-07-27 — the name was showing verbatim in both places).
         Kept as a plain button for a fast exit from the bottom of a long nav list. -->
    <ng-template pTemplate="footer">
      <div class="tms-sb-foot">
        <!-- Pinned bar: always in view (Settings opens a pop-up list, Support opens its page, Sync Issues only when there are some) -->
        <div class="tms-sb-pins" *ngIf="pinnedMenuItems.length">
          <button
            type="button"
            class="tms-sb-pin"
            *ngFor="let p of pinnedMenuItems; trackBy: trackNav"
            [class.is-active]="isNavActive(p)"
            [attr.aria-haspopup]="p.items?.length ? 'menu' : null"
            (click)="openPinned($event, p)"
          >
            <ll-icon *ngIf="menuStyle.dockOn(); else pinPi" [name]="p.icon"></ll-icon>
            <ng-template #pinPi><i [ngClass]="p.icon"></i></ng-template>
            <span class="tms-sb-pin-label">{{ p.label }}</span>
            <i class="pi pi-chevron-up tms-sb-pin-caret" *ngIf="p.items?.length"></i>
          </button>
        </div>
        <!-- Bottom: the PERSON (photo, name, email). It opens the account pop-up: Edit Profile, Storage, Tour, Feedback, Logout. -->
        <button type="button" class="tms-sb-acct" aria-haspopup="menu" (click)="accountMenu.toggle($event)">
          <img *ngIf="getAvatarImgSrc() as avatarSrc; else acctInitials" class="tms-sb-acct-avatar" [src]="avatarSrc" alt="" (error)="onAvatarImageError()" />
          <ng-template #acctInitials><span class="tms-sb-acct-avatar" aria-hidden="true">{{ userProfile?.firstName?.charAt(0) || 'U' }}</span></ng-template>
          <span class="tms-sb-acct-text">
            <span class="tms-sb-acct-name">{{ userProfile?.firstName ?? '' }} {{ userProfile?.lastName ?? '' }}</span>
            <span class="tms-sb-acct-sub">{{ userProfile?.email ?? '' }}</span>
          </span>
          <i class="pi pi-chevron-up" aria-hidden="true"></i>
        </button>
      </div>
    </ng-template>
  </p-drawer>
</ng-container>

<!-- ══ Docked menu (2026-10-07) ═════════════════════════════════════════════════════════════════════════════════════════
     Phone: bottom dock (+ "More" = the drawer above, as a sheet). Tablet and wider: side rail. Only while the person chose the dock
     style (avatar menu); the classic drawer is untouched. Same menu, same rights, nothing new is fetched. -->
<ng-container *ngIf="router.url !== '/login'">
  <nav class="tms-dock" *ngIf="menuStyle.dockShown()" [attr.aria-label]="'menuDock.navLabel' | t">
    <button type="button" class="tms-dock-hide" (click)="menuStyle.setBars('dock-hidden')" [attr.aria-label]="'menuDock.hideBar' | t" [attr.title]="'menuDock.hideBar' | t">
      <ll-icon name="chevron-down"></ll-icon>
    </button>
    <div class="tms-dock-row" [class.has-create]="dockView.create">
      <button type="button" class="tms-dock-item" *ngFor="let s of dockView.left; trackBy: trackRecent"
        [class.is-active]="isDockActive(s)" [attr.aria-current]="isDockActive(s) ? 'page' : null" (click)="openDockSlot(s)">
        <ll-icon [name]="s.icon"></ll-icon>
        <span class="tms-dock-label">{{ s.label }}</span>
      </button>
      <button type="button" class="tms-dock-plus" *ngIf="dockView.create" aria-haspopup="menu" [attr.aria-label]="'sidebar.new' | t" (click)="openNewMenu($event)">
        <ll-icon name="plus"></ll-icon>
      </button>
      <button type="button" class="tms-dock-item" *ngFor="let s of dockView.right; trackBy: trackRecent"
        [class.is-active]="isDockActive(s)" [attr.aria-current]="isDockActive(s) ? 'page' : null" (click)="openDockSlot(s)">
        <ll-icon [name]="s.icon"></ll-icon>
        <span class="tms-dock-label">{{ s.label }}</span>
      </button>
      <button type="button" class="tms-dock-item" [class.is-active]="isMenuExpand" aria-haspopup="dialog" (click)="openMore()">
        <ll-icon name="th-large"></ll-icon>
        <span class="tms-dock-label">{{ 'menuDock.more' | t }}</span>
      </button>
    </div>
  </nav>

  <!-- Dock hidden by hand: a thin strip in the page flow (never floating, so it can never cover a status bar or button) brings it back. -->
  <button type="button" class="tms-dock-strip" *ngIf="menuStyle.stripShown()" (click)="menuStyle.restoreBars()"
    [attr.aria-label]="(menuStyle.focusActive() ? 'menuDock.showBars' : 'menuDock.showBottomBar') | t">
    <ll-icon name="chevron-down" class="up"></ll-icon>
    <span>{{ (menuStyle.focusActive() ? 'menuDock.showBars' : 'menuDock.showBottomBar') | t }}</span>
  </button>

  <ng-container *ngIf="menuStyle.railDesktop()">
    <nav class="tms-rail" [class.is-wide]="railWide" [class.is-float]="railWide && !railPinned" (mouseenter)="onRailEnter()" (mouseleave)="onRailLeave()"
      [attr.aria-label]="'menuDock.navLabel' | t">
      <button type="button" class="tms-rail-co" aria-haspopup="menu" [attr.title]="activeCompanyDisplayName || (userProfile?.companyName ?? '')" (click)="workspaceMenu.toggle($event)">
        <span class="tms-rail-badge" aria-hidden="true">{{ companyInitials }}</span>
        <span class="tms-rail-co-text">
          <b>{{ companyTier().main }}</b>
          <i *ngIf="companyTier().sub">{{ companyTier().sub }}</i>
        </span>
        <ll-icon name="chevron-down" class="tms-rail-caret"></ll-icon>
      </button>

      <div class="tms-rail-tools">
        <button type="button" class="tms-rail-new" *ngIf="canCreateAnything()" aria-haspopup="menu" [attr.aria-label]="'sidebar.new' | t" (click)="openNewMenu($event)">
          <ll-icon name="plus"></ll-icon><span>{{ 'sidebar.new' | t }}</span>
        </button>
        <label class="tms-rail-search" (click)="openRailSearch()">
          <ll-icon name="search"></ll-icon>
          <input #railSearch type="search" autocomplete="off" [value]="searchString" (input)="onRailSearchInput($any($event.target).value)" (blur)="onRailSearchBlur()"
            [placeholder]="'layout.searchMenu' | t" [attr.aria-label]="'layout.searchMenu' | t" />
        </label>
        <!-- Pin (2026-10-08): keeps the open menu in place (content moves over); unpinned, the open menu floats and closes when you click away. -->
        <button type="button" class="tms-rail-keep" *ngIf="railWide" [class.is-on]="railPinned" (click)="toggleRailPin()" [attr.aria-pressed]="railPinned"
          [attr.title]="(railPinned ? 'menuDock.railUnpin' : 'menuDock.railPin') | t" [attr.aria-label]="(railPinned ? 'menuDock.railUnpin' : 'menuDock.railPin') | t">
          <ll-icon name="pin"></ll-icon>
        </button>
      </div>

      <div class="tms-rail-scroll">
        <ng-container *ngFor="let it of accessibleMenuItems; trackBy: trackNav">
          <div class="tms-rail-cap" role="presentation" *ngIf="it.isHeading"><span>{{ it.label }}</span></div>
          <ng-container *ngIf="!it.isHeading">
            <button type="button" class="tms-rail-row" [class.is-active]="isNavActive(it) || railGroupActive(it)"
              [attr.aria-expanded]="it.items?.length ? !!it.expanded : null" [attr.aria-current]="isNavActive(it) ? 'page' : null" [attr.title]="it.label"
              (click)="onRailRowClick($event, it)">
              <span class="tms-rail-ico"><ll-icon [name]="it.icon"></ll-icon></span>
              @let tp = tierParts(it.label);
              <span class="tms-rail-text">
                <span class="t1"><ng-container *ngFor="let p of labelParts(tp.main)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span>
                <span class="t2" *ngIf="tp.sub"><ng-container *ngFor="let p of labelParts(tp.sub)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span>
              </span>
              <span class="tms-nav-badge" *ngIf="it.badge">{{ it.badge }}</span>
              <ll-icon name="chevron-down" class="tms-rail-gcar" [class.open]="it.expanded" *ngIf="it.items?.length"></ll-icon>
            </button>
            <div class="tms-rail-kids" *ngIf="it.items?.length && it.expanded && railWide">
              <button type="button" class="tms-rail-row tms-rail-sub" *ngFor="let c of it.items; trackBy: trackNav"
                [class.is-active]="isNavActive(c)" [attr.aria-current]="isNavActive(c) ? 'page' : null" [attr.title]="c.label" (click)="onRailRowClick($event, c)">
                @let ct = tierParts(c.label);
                <span class="tms-rail-text">
                  <span class="t1"><ng-container *ngFor="let p of labelParts(ct.main)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span>
                  <span class="t2" *ngIf="ct.sub"><ng-container *ngFor="let p of labelParts(ct.sub)"><mark class="tms-nav-hit" *ngIf="p.hit">{{ p.text }}</mark><ng-container *ngIf="!p.hit">{{ p.text }}</ng-container></ng-container></span>
                </span>
                <span class="tms-nav-badge" *ngIf="c.badge">{{ c.badge }}</span>
              </button>
            </div>
          </ng-container>
        </ng-container>
      </div>

      <div class="tms-rail-foot">
        <div class="tms-rail-pins">
          <button type="button" class="tms-rail-pin" *ngFor="let p of pinnedMenuItems; trackBy: trackNav" [class.is-active]="isNavActive(p)"
            [attr.aria-haspopup]="p.items?.length ? 'menu' : null" [attr.title]="p.label" (click)="openPinned($event, p)">
            <ll-icon [name]="p.icon"></ll-icon>
            <span class="t">{{ p.label }}</span>
            <ll-icon name="chevron-up" class="up" *ngIf="p.items?.length"></ll-icon>
          </button>
        </div>
        <button type="button" class="tms-rail-acct" aria-haspopup="menu" (click)="accountMenu.toggle($event)">
          <img *ngIf="getAvatarImgSrc() as railAvatar; else railInitials" class="tms-rail-avatar" [src]="railAvatar" alt="" (error)="onAvatarImageError()" />
          <ng-template #railInitials><span class="tms-rail-avatar" aria-hidden="true">{{ userProfile?.firstName?.charAt(0) || 'U' }}</span></ng-template>
          <span class="tms-rail-acct-text">
            <b>{{ userProfile?.firstName ?? '' }} {{ userProfile?.lastName ?? '' }}</b>
            <i>{{ userProfile?.email ?? '' }}</i>
          </span>
          <ll-icon name="chevron-up" class="up"></ll-icon>
        </button>
      </div>
      <!-- Menu toggle (2026-10-07): a slim handle on the rail's right edge, so it takes no row. » opens the menu and keeps it open; « puts it back to icons. -->
      <button type="button" class="tms-rail-handle" (click)="toggleRailPin()" [attr.aria-pressed]="railPinned"
        [attr.title]="(railPinned ? 'menuDock.railUnpin' : 'menuDock.railPin') | t" [attr.aria-label]="(railPinned ? 'menuDock.railUnpin' : 'menuDock.railPin') | t">
        <ll-icon [name]="railPinned ? 'chevron-left' : 'chevron-right'"></ll-icon>
      </button>
    </nav>
    <button type="button" class="tms-rail-edge" *ngIf="menuStyle.railMode() === 'hidden'" (click)="showRail()" [attr.aria-label]="'menuDock.railShow' | t" [attr.title]="'menuDock.railShow' | t">
      <ll-icon name="chevron-right"></ll-icon>
    </button>
  </ng-container>
</ng-container>

<!-- Deliberately OUTSIDE p-drawer, not nested in its header template: a p-menu portal
     nested inside another animated overlay component's (p-drawer) template coupled their
     lifecycles — closing the drawer mid-animation while this menu's own overlay was still
     active could orphan a stray mask/portal element that silently ate clicks afterward
     (same class of bug as the documented offline-freeze issue elsewhere in this app). As a
     sibling here, its overlay lifecycle is fully independent, matching HeaderComponent's
     own (never-nested) p-menu, which has never shown this symptom. The #accountMenu
     reference is still resolvable from inside p-drawer's header template above — Angular
     resolves template variables via the declaration-view chain, not DOM nesting depth. -->
<p-menu #accountMenu [popup]="true" [model]="accountMenuItems" appendTo="body" styleClass="tms-sb-pop">
  <ng-template pTemplate="start">
    <div class="tms-pop-id">
      <img *ngIf="getAvatarImgSrc() as popAvatar; else popInitials" class="tms-pop-avatar" [src]="popAvatar" alt="" (error)="onAvatarImageError()" />
      <ng-template #popInitials><span class="tms-pop-avatar" aria-hidden="true">{{ userProfile?.firstName?.charAt(0) || 'U' }}</span></ng-template>
      <span class="tms-pop-id-text">
        <span class="tms-pop-id-name">{{ userProfile?.firstName ?? '' }} {{ userProfile?.lastName ?? '' }}</span>
        <span class="tms-pop-id-sub">{{ userProfile?.email ?? '' }}</span>
      </span>
    </div>
  </ng-template>
  <ng-template pTemplate="end">
    <div class="tms-pop-meta">SB {{ userProfile?.appVersion ?? '' }} · NG {{ ngAppVersion }}</div>
  </ng-template>
</p-menu>
<p-menu #workspaceMenu [popup]="true" [model]="workspaceMenuItems" appendTo="body" styleClass="tms-sb-pop">
  <ng-template pTemplate="start">
    <div class="tms-pop-ws">
      <span class="tms-pop-ws-name">{{ activeCompanyDisplayName || userProfile?.companyName || '' }}</span>
      <button
        type="button"
        class="tms-pop-chip"
        *ngIf="userProfile?.companyId"
        [class.is-copied]="idCopied"
        [attr.aria-label]="'layout.copyCompanyId' | t"
        (click)="copyCompanyId(); $event.stopPropagation()"
      >
        <span>{{ idCopied ? ('sidebar.copied' | t) : userProfile?.companyId }}</span>
        <i class="pi" [ngClass]="idCopied ? 'pi-check' : 'pi-copy'"></i>
      </button>
    </div>
  </ng-template>
</p-menu>
<p-menu #newMenu [popup]="true" [model]="newMenuItems" appendTo="body" styleClass="tms-sb-pop"></p-menu>
<p-menu #pinnedMenu [popup]="true" [model]="pinnedPopupItems" appendTo="body" styleClass="tms-sb-pop tms-sb-pin-menu"></p-menu>

<p-confirmDialog key="sidebarLogoutConfirm" styleClass="ll-confirm-big"></p-confirmDialog>
<p-confirmDialog key="sidebarSwitchCompanyConfirm" [style]="{ width: '28rem' }" [breakpoints]="{ '640px': '92vw' }"></p-confirmDialog>
